import type { TOC } from '@ember/component/template-only';
import type { ItemDetailsSignature } from './utils.gts';
import {
  flagUrl,
  flagUrl2x,
  formatRating,
  priceSymbol,
} from '../../utils/format';

interface PlaceData {
  name?: string;
  formattedAddress?: string;
  address?: string;
  editorialSummary?: string;
  rating?: number;
  userRatingCount?: number;
  priceLevel?: string | number;
  primaryType?: string;
  countryCode?: string;
  location?: {
    address?: string;
    formattedAddress?: string;
    latitude: number;
    longitude: number;
  };
}

/** Card details for places: address, distance, rating, price, type */
const PlaceDetails: TOC<ItemDetailsSignature> = <template>
  {{#let (asPlaceData @data) as |d|}}
    {{#if (address d)}}
      <p class="text-base-content/60 mt-1 line-clamp-2 text-sm">
        {{#if d.countryCode}}
          <img
            src={{flagUrl d.countryCode}}
            srcset="{{flagUrl d.countryCode}} 1x, {{flagUrl2x
              d.countryCode
            }} 2x"
            width="16"
            alt={{d.countryCode}}
            class="mr-1 inline-block rounded-sm align-text-bottom shadow-sm"
          />
        {{/if}}
        {{address d}}
        {{#if @distance}}
          <span class="text-base-content/50 ml-1">• {{@distance}}</span>
        {{/if}}
      </p>
    {{/if}}

    {{#if (hasExtraDetails d)}}
      <div class="mt-2 flex flex-wrap gap-2">
        {{#if d.rating}}
          <div class="flex items-center gap-1">
            <span class="text-warning font-bold">★</span>
            <span class="text-sm font-semibold">{{formatRating d.rating}}</span>
            {{#if d.userRatingCount}}
              <span
                class="text-base-content/50 text-xs"
              >({{d.userRatingCount}})</span>
            {{/if}}
          </div>
        {{/if}}
        {{#if d.priceLevel}}
          <span class="badge badge-outline badge-sm">{{priceSymbol
              d.priceLevel
            }}</span>
        {{/if}}
        {{#if d.primaryType}}
          <span
            class="badge badge-ghost badge-sm capitalize"
          >{{d.primaryType}}</span>
        {{/if}}
      </div>
    {{/if}}
  {{/let}}
</template>;

function asPlaceData(
  raw: Record<string, unknown> | null | undefined
): PlaceData {
  return raw ?? {};
}

function hasExtraDetails(d: PlaceData): boolean {
  return !!(d.rating || d.priceLevel || d.primaryType);
}

function address(data: PlaceData | null): string | null {
  if (!data) return null;
  const loc = data.location;
  return (loc?.address ??
    loc?.formattedAddress ??
    data.address ??
    data.formattedAddress) as string | null;
}

export default PlaceDetails;
